import { useEffect } from 'react';
import { useStorage } from '../../../../context/storage-context';
import { useStorageKey } from '../../../../context/use-storage-key';
import { useAddSkuToCart } from '../../../customers/hooks/use-add-sku-to-cart';
import { CUSTOMER_LOOKUP } from '../../../customers/hooks/use-get-customer';
import { useTrackedCustomer } from '../customer/use-tracked-customer';
import { useSellableSku } from './use-sellable-sku';

export type CartPrefillStatus =
  /** Resolving a sellable product / the add is in flight. */
  | 'adding'
  /** A product is in the buyer's cart. */
  | 'added'
  /** Nothing was added (no sellable product, or the add failed) — the buyer adds one themselves. */
  | 'manual';

/**
 * Eagerly prefills the tracked customer's cart with a sellable product before the buyer leaves for
 * the storefront. Runs once per customer — the add endpoint adds a unit on every call with no cart
 * read to dedupe against, so the prefilled customer is remembered in local storage across remounts.
 */
export function useCartPrefill(): CartPrefillStatus {
  const key = useStorageKey('tutorial');
  const { customer } = useTrackedCustomer();
  const sku = useSellableSku();
  const { mutate, isIdle, isSuccess, isError } = useAddSkuToCart();
  const [prefilledCustomerId, setPrefilledCustomerId] = useStorage<string>(
    key('prefilled-cart-customer'),
  );

  const alreadyPrefilled = !!customer && prefilledCustomerId === customer.id;

  useEffect(() => {
    if (!customer || !sku || alreadyPrefilled || !isIdle) {
      return;
    }
    mutate(
      { lookup: CUSTOMER_LOOKUP.sparklayer(customer.id), sku },
      { onSuccess: () => setPrefilledCustomerId(customer.id) },
    );
  }, [customer, sku, alreadyPrefilled, isIdle, mutate, setPrefilledCustomerId]);

  if (alreadyPrefilled || isSuccess) {
    return 'added';
  }
  if (isError || sku === null) {
    return 'manual';
  }
  return 'adding';
}
